import { format } from 'date-fns';
import { EntityType } from '@ruta/shared';
import { searchCustomers, searchSubscriptions, searchVisits } from '@/lib/search-api';
import { getServiceTemplates } from '@/lib/api/routes/service-templates';

// The lightweight reference attached to an outgoing chat message. Mirrors
// `AssistantEntityReference` on the API (apps/api/.../assistant/types.ts).
export interface AssistantEntityReference {
  entityType: EntityType | string;
  entityId: string;
  displayName?: string;
  customerId?: string;
  serviceTemplateId?: string;
}

export type MentionTab = 'services' | 'visits' | 'customers';

// One row in the @-mention picker (a superset of the reference we send).
export interface MentionOption {
  entityType: EntityType;
  entityId: string;
  displayName: string;
  subtitle?: string;
  customerId?: string;
  serviceTemplateId?: string;
}

export function toReference(option: MentionOption): AssistantEntityReference {
  return {
    entityType: option.entityType,
    entityId: option.entityId,
    displayName: option.displayName,
    customerId: option.customerId,
    serviceTemplateId: option.serviceTemplateId,
  };
}

function visitStatusLabel(status?: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'Completed';
    case 'NOT_STARTED':
      return 'Scheduled';
    case 'IN_PROGRESS':
      return 'In progress';
    case 'PAID':
      return 'Paid';
    case 'CANCELLED':
      return 'Cancelled';
    case 'SKIPPED':
      return 'Skipped';
    default:
      return 'Pending';
  }
}

function joinMeta(parts: Array<string | undefined | null>): string | undefined {
  const kept = parts.filter((p): p is string => !!p && p.trim().length > 0);
  return kept.length ? kept.join(' · ') : undefined;
}

// Services tab is context-aware, matching mobile: no query browses the distinct
// service-template catalog (types of service); typing searches real per-customer
// subscriptions so "@kurt" surfaces Kurt's services.
async function searchServiceTemplates(query: string): Promise<MentionOption[]> {
  try {
    const templates = await getServiceTemplates({ includeInactive: true });
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? templates.filter(
          (t) =>
            (t.name || '').toLowerCase().includes(needle) ||
            (t.category || '').toLowerCase().includes(needle),
        )
      : templates;
    return filtered.slice(0, 25).map((t) => ({
      entityType: EntityType.SERVICE_TEMPLATE,
      entityId: t.id,
      displayName: t.name || 'Service',
      subtitle: joinMeta([t.category, t.isActive === false ? 'Inactive' : undefined]),
    }));
  } catch {
    return [];
  }
}

async function searchSubscriptionOptions(query: string): Promise<MentionOption[]> {
  try {
    const { hits } = await searchSubscriptions({ q: query.trim(), per_page: 25 });
    return (hits ?? []).map((h: any) => ({
      entityType: EntityType.SUBSCRIPTION,
      entityId: h.id,
      displayName: h.serviceTemplateName || 'Service',
      subtitle: joinMeta([
        h.subscriptionStatus === 'ACTIVE' ? 'Active' : h.subscriptionStatus ? 'Inactive' : undefined,
        h.customerName,
      ]),
      customerId: h.customerId,
      serviceTemplateId: h.serviceTemplateId,
    }));
  } catch {
    return [];
  }
}

async function searchServices(query: string): Promise<MentionOption[]> {
  return query.trim() ? searchSubscriptionOptions(query) : searchServiceTemplates('');
}

async function searchVisitOptions(query: string): Promise<MentionOption[]> {
  try {
    const { hits } = await searchVisits({ q: query.trim(), per_page: 25 });
    return (hits ?? []).map((h: any) => {
      const date = h.createdAt ? format(new Date(h.createdAt), 'MMM d') : null;
      return {
        entityType: EntityType.VISIT,
        entityId: h.id,
        displayName: joinMeta([date, h.serviceName || 'Visit']) || 'Visit',
        subtitle: joinMeta([visitStatusLabel(h.status), h.customerDisplayName]),
        customerId: h.customerId,
      };
    });
  } catch {
    return [];
  }
}

async function searchCustomerOptions(query: string): Promise<MentionOption[]> {
  try {
    const { hits } = await searchCustomers({ q: query.trim(), per_page: 25 });
    return (hits ?? []).map((h: any) => ({
      entityType: EntityType.CUSTOMER,
      entityId: h.id,
      displayName: `${h.firstName || ''} ${h.lastName || ''}`.trim() || 'Customer',
      subtitle: h.address || undefined,
      customerId: h.id,
    }));
  } catch {
    return [];
  }
}

export function searchMentions(tab: MentionTab, query: string): Promise<MentionOption[]> {
  switch (tab) {
    case 'services':
      return searchServices(query);
    case 'visits':
      return searchVisitOptions(query);
    case 'customers':
      return searchCustomerOptions(query);
  }
}
